import React, { useState } from "react";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { ChevronDown, ChevronRight } from "@dreamhub/designsystem/icons";

export interface CollapseProps {
  triggerText?: string;
  trigger?: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

const Collapse: React.FC<CollapseProps> = ({
  triggerText,
  trigger,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalIsOpen;

  const handleToggle = () => {
    const newValue = !isOpen;
    if (isControlled) {
      onOpenChange?.(newValue);
    } else {
      setInternalIsOpen(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  const triggerContent = trigger ? (
    typeof trigger === "function" ? (
      trigger(isOpen)
    ) : (
      trigger
    )
  ) : (
    <>
      <IconContainer
        size="5"
        color="primary"
        className="transition-all duration-200 ease-in-out"
      >
        {isOpen ? <ChevronDown /> : <ChevronRight />}
      </IconContainer>
      <Text variant="body">{triggerText}</Text>
    </>
  );

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          trigger
            ? ""
            : "flex items-center gap-2 h-8 px-[10px] py-[6px] cursor-pointer transition-all duration-200 ease-in-out w-fit hover:rounded-lg hover:bg-surface-hover-strong"
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-controls="collapse-content"
      >
        {triggerContent}
      </div>

      <div
        id="collapse-content"
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          trigger
            ? isOpen
              ? "grid-rows-[1fr]"
              : "grid-rows-[0fr]"
            : isOpen
              ? "grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          {trigger ? children : <div className="pt-4 px-8">{children}</div>}
        </div>
      </div>
    </div>
  );
};

export default Collapse;
